/**
 * Extracts the developer version from the current frontend hostname.
 * Versioned staging URL: `<version>.beta-devtools.msgsndr.net` (e.g. sujith.beta-devtools.msgsndr.net → "sujith").
 * Returns null on localhost, the default (unversioned) host, or in non-browser environments.
 *
 * The full subdomain is returned as-is — the platform's documented 10-char "soft" limit is
 * advisory, and real deployments use longer values (see docs/PACKAGE_DETAILS.md).
 */
export function getDeveloperVersion(): string | null {
  if (typeof window === 'undefined') return null
  const hostname = window.location.hostname
  const match = hostname.match(/^([a-z0-9-]+)\.beta-devtools\.msgsndr\.net$/)
  return match ? match[1] : null
}

/**
 * Decides whether a `developer_version` header is appropriate for the given
 * outbound request URL. Versioned backend deployments only exist in staging/dev,
 * so we never send the header to production hosts.
 *
 * Returns true for:
 *  - Relative URLs (same-origin → frontend host is already version-gated by `getDeveloperVersion`)
 *  - Local dev / proxy targets (localhost, 127.0.0.1)
 *  - Known staging/dev hosts (`dev.services...`, `staging.services...`)
 *  - Other `*.beta-devtools.msgsndr.net` subdomains
 *
 * Returns false for any other absolute URL — most importantly `services.leadconnectorhq.com`
 * (production) and unparseable URLs.
 */
export function shouldSendDeveloperVersion(requestUrl: string | undefined | null): boolean {
  if (!requestUrl) return true

  // Detect absolute or scheme-relative URLs (`https://...`, `//host/...`, etc.).
  // Anything else is treated as same-origin (frontend host is already gated by `getDeveloperVersion`).
  const isAbsoluteLike = /^(?:[a-z][a-z\d+.-]*:)?\/\//i.test(requestUrl)
  if (!isAbsoluteLike) return true

  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://invalid.local'
    const host = new URL(requestUrl, base).hostname.toLowerCase()
    if (host === 'localhost' || host === '127.0.0.1') return true
    if (host === 'dev.services.leadconnectorhq.com') return true
    if (host === 'staging.services.leadconnectorhq.com') return true
    if (host.endsWith('.beta-devtools.msgsndr.net')) return true
    return false
  } catch {
    return false
  }
}
